import type { GrammarExercise } from '../exercise-types';

// Advanced-level practice exercises, one entry per advanced-tenses topic.
// Written after the founder-approved pilot in ../exercises.ts; the
// 'second-conditional' entry is copied verbatim from that pilot.
export const advancedTensesExercises: Record<string, GrammarExercise[]> = {
  'future-continuous': [
    {
      kind: 'multiple-choice',
      prompt: 'This time next year, Marina ___ her own design studio in São Paulo.',
      options: ['will be running', 'will run', 'will have run'],
      correctIndex: 0,
      explanation:
        'The future continuous (will + be + -ing) describes an action in progress at a particular future time.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'After the course, Júlia will be knowing how to lead a negotiation.',
        'After the course, Júlia will know how to lead a negotiation.',
        'After the course, Júlia will be know how to lead a negotiation.',
      ],
      correctIndex: 1,
      explanation: 'The future continuous is used with action verbs only — know is a state verb.',
    },
    {
      kind: 'fill-blank',
      prompt: 'At nine tomorrow, I ___ (present) my project to the board — wish me luck!',
      answers: ['will be presenting', '’ll be presenting', "'ll be presenting"],
      explanation: 'Will + be + the -ing form shows the presentation will be in progress at nine.',
    },
    {
      kind: 'multiple-choice',
      prompt:
        'I ___ at five o’clock, and then I will be meeting with the management about my raise.',
      options: ['will arrive', 'will be arriving', 'will be arrive'],
      correctIndex: 0,
      explanation:
        'Arriving happens once and is completed, so the simple future fits; the meeting has duration, so it takes the continuous.',
    },
    {
      kind: 'matching',
      instruction: 'Match each sentence with the reason for its future form:',
      pairs: [
        { left: 'She will arrive at five.', right: 'single completed future action' },
        { left: 'She will be negotiating at five.', right: 'action in progress at a future time' },
        { left: 'She will be confident tomorrow.', right: 'state verb — no continuous form' },
      ],
    },
    {
      kind: 'multiple-choice',
      prompt: 'Camila ___ working on Friday — she’ll be celebrating her graduation instead.',
      options: ['won’t be', 'doesn’t be', 'isn’t'],
      correctIndex: 0,
      explanation: 'The negative of the future continuous is will not (won’t) + be + -ing.',
    },
  ],

  'future-perfect': [
    {
      kind: 'fill-blank',
      prompt: 'By June, Renata ___ (work) at her own company for ten years.',
      answers: ['will have worked', '’ll have worked', "'ll have worked"],
      explanation:
        'The future perfect (will have + participle) looks back at an action completed before a future point.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'By the time you read this email, I ___ the contract.',
      options: ['will have signed', 'will sign', 'have signed'],
      correctIndex: 0,
      explanation:
        '“By the time...” sets a future deadline, so the completed action takes the future perfect.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'Linda will have left.',
        'By eight o’clock, Linda will have left.',
        'Linda will have leave by eight o’clock.',
      ],
      correctIndex: 1,
      explanation:
        'The future perfect needs a deadline — without one, use the simple future (Linda will leave).',
    },
    {
      kind: 'fill-blank',
      prompt: '___ you have finished your master’s degree by December? (one word)',
      answers: ['will'],
      explanation: 'Questions in the future perfect use Will + subject + have + participle.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'When she travels to Japan, Beatriz will have ___ to ten countries.',
      options: ['been', 'be', 'being'],
      correctIndex: 0,
      explanation: 'After will have, the main verb goes in the participle form: been.',
    },
  ],

  'past-habits-used-to-would': [
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'Luiza would have long hair when she was younger.',
        'Luiza used to have long hair when she was younger.',
        'Luiza would had long hair when she was younger.',
      ],
      correctIndex: 1,
      explanation:
        'Have here is a state, and would is only used with action verbs — states take used to.',
    },
    {
      kind: 'fill-blank',
      prompt:
        'Every Sunday, my grandmother ___ tell us stories about the strong women in our family. (one word)',
      answers: ['would', '’d', "'d"],
      explanation: 'Would + infinitive describes repeated past actions, especially in stories.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'There ___ be so many women leading tech meetings — that is finally changing.',
      options: ['didn’t use to', 'didn’t used to', 'wouldn’t use to'],
      correctIndex: 0,
      explanation: 'The negative is didn’t use to, without the final -d.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which question is correct?',
      options: [
        'Did you use to doubt your English?',
        'Did you used to doubt your English?',
        'Would you use to doubt your English?',
      ],
      correctIndex: 0,
      explanation:
        'Questions take Did + subject + use to — and we don’t normally use would in questions about past habits.',
    },
    {
      kind: 'fill-blank',
      prompt:
        'I ___ (use) to believe my voice didn’t matter — now I speak up in every meeting.',
      answers: ['used'],
      explanation: 'Used to + infinitive describes a past state that is not true anymore.',
    },
    {
      kind: 'matching',
      instruction: 'Match each form with its rule:',
      pairs: [
        { left: 'used to', right: 'past states and repeated past actions' },
        { left: 'would', right: 'repeated past actions only, slightly more formal' },
        { left: 'didn’t use to', right: 'the negative form' },
      ],
    },
  ],

  'past-continuous': [
    {
      kind: 'multiple-choice',
      prompt:
        'Fernanda ___ her presentation when the director walked in — and she kept going, confident.',
      options: ['was rehearsing', 'rehearsed', 'is rehearsing'],
      correctIndex: 0,
      explanation:
        'The past continuous describes an action in progress when another past event occurred.',
    },
    {
      kind: 'fill-blank',
      prompt: 'When the phone rang, I ___ (study) for my English test.',
      answers: ['was studying'],
      explanation: 'The unfinished action (was studying) was interrupted by the event (rang).',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I ___ if you could review my CV tonight.',
      options: ['was wondering', 'wondered', 'am wonder'],
      correctIndex: 0,
      explanation: 'I was wondering if... makes a very polite request.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I ___ to accept the first offer, but I decided to negotiate a better salary instead.',
      options: ['was going', 'went', 'am going'],
      correctIndex: 0,
      explanation: 'Was going to expresses a change of mind about a past plan.',
    },
    {
      kind: 'matching',
      instruction: 'Match each sentence with the use of the past continuous it shows:',
      pairs: [
        {
          left: 'The sun was shining as she walked to her interview.',
          right: 'background of a story',
        },
        { left: 'She was sleeping when the alarm rang.', right: 'interrupted action' },
        { left: 'I was wondering if you could help me.', right: 'very polite request' },
      ],
    },
    {
      kind: 'fill-blank',
      prompt: 'Ana ___ (drive) to work when she heard the news about her promotion on the phone.',
      answers: ['was driving'],
      explanation:
        'The longer action in progress takes the past continuous; the event that interrupts it takes the simple past.',
    },
  ],

  'present-perfect': [
    {
      kind: 'multiple-choice',
      prompt: 'Patrícia ___ her own business for eight years — and it keeps growing.',
      options: ['has run', 'runs', 'ran'],
      correctIndex: 0,
      explanation:
        'The present perfect shows something that started in the past and is still true now.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'I have finished the report yesterday.',
        'I finished the report yesterday.',
        'I have finish the report yesterday.',
      ],
      correctIndex: 1,
      explanation:
        'With a finished time word like yesterday, we use the simple past, not the present perfect.',
    },
    {
      kind: 'fill-blank',
      prompt: '___ you ever spoken English in a job interview? (one word)',
      answers: ['have'],
      explanation: 'Questions about life experiences use Have + subject + participle, often with ever.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Mariana has ___ (write) three articles this month — and the month isn’t over.',
      answers: ['written'],
      explanation: 'After have/has, the verb goes in the participle form: write → written.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I can’t find my badge. I ___ it somewhere in the office.',
      options: ['have lost', 'lose', 'was losing'],
      correctIndex: 0,
      explanation: 'The present perfect connects a past event to its result in the present.',
    },
    {
      kind: 'matching',
      instruction: 'Match each verb with its past participle:',
      pairs: [
        { left: 'to grow', right: 'grown' },
        { left: 'to build', right: 'built' },
        { left: 'to win', right: 'won' },
      ],
      explanation: 'Irregular participles come from the third column of the verb chart.',
    },
  ],

  'transcendent-aspect': [
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence suggests the achievement still matters in the present?',
      options: ['She has built a strong team.', 'She built a strong team.', 'She builds a strong team.'],
      correctIndex: 0,
      explanation:
        'The transcendent (perfect) aspect puts the speaker in the result phase — the achievement extends to now.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Gabriela ___ two new clients this week — and the week isn’t over.',
      options: ['has signed', 'signed', 'sign'],
      correctIndex: 0,
      explanation:
        'With unfinished time words like this week, we use the present perfect.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ lost my keys — that’s why I can’t open the door. (one word)',
      answers: ['have', '’ve', "'ve"],
      explanation: 'The perfect focuses on the result of the past event: no keys, no open door.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Last year, Paula ___ her first conference talk, and the audience loved it.',
      options: ['gave', 'has given', 'has gave'],
      correctIndex: 0,
      explanation:
        'When we are simply telling a past story, the focus is on the event itself — simple past.',
    },
    {
      kind: 'matching',
      instruction: 'Match each sentence with its focus:',
      pairs: [
        { left: 'I’ve been to Paris twice.', right: 'result that reaches the present' },
        { left: 'I went to Paris in 2019.', right: 'finished past story' },
        { left: 'I’ve worked hard today.', right: 'unfinished time word' },
      ],
    },
  ],

  'past-perfect': [
    {
      kind: 'multiple-choice',
      prompt: 'By the time Renata got the promotion, she ___ for it for two years.',
      options: ['had prepared', 'has prepared', 'prepares'],
      correctIndex: 0,
      explanation:
        'The past perfect (had + participle) marks the earlier of two past events — the past of the past.',
    },
    {
      kind: 'fill-blank',
      prompt: 'The interview went well because Carol ___ (practice) her answers the night before.',
      answers: ['had practiced', 'had practised', '’d practiced', "'d practiced"],
      explanation: 'Practicing happened before the interview, so it takes had + participle.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'She was proud because she had finished the course.',
        'She was proud because she has finished the course.',
        'She had been proud because she had finish the course.',
      ],
      correctIndex: 0,
      explanation:
        'Finishing came first, being proud came second — the earlier event takes the past perfect.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ never led a team before I took that job — now I lead twenty people. (one word)',
      answers: ['had', '’d', "'d"],
      explanation: 'Had + participle shows the situation existed before another point in the past.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'When Luísa opened her bag at the airport, she realized she ___ her passport at home.',
      options: ['had left', 'has left', 'leaves'],
      correctIndex: 0,
      explanation:
        'Leaving the passport happened before realizing it — the sequence calls for the past perfect.',
    },
  ],

  'present-perfect-continuous': [
    {
      kind: 'multiple-choice',
      prompt: 'Duda ___ English for six months, and she can already lead a meeting.',
      options: ['has been studying', 'is studying', 'studied'],
      correctIndex: 0,
      explanation:
        'Has/have been + -ing shows something started in the past and is still continuing now.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ been feeling more confident lately. (one word)',
      answers: ['have', '’ve', "'ve"],
      explanation: 'The present perfect continuous is formed with have/has been + the -ing form.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence tells us she is still reading the report?',
      options: [
        'She has been reading the report.',
        'She has read the report.',
        'She read the report.',
      ],
      correctIndex: 0,
      explanation:
        'The continuous aspect shows the activity began in the past and is not yet finished.',
    },
    {
      kind: 'fill-blank',
      prompt: 'How long have you ___ (work) on this project?',
      answers: ['been working'],
      explanation: 'After have/has comes been + the gerund: have been working.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Helena ___ been mentoring younger women at her company recently.',
      options: ['has', 'have', 'had'],
      correctIndex: 0,
      explanation:
        'Recently and lately often appear with the present perfect continuous — with she, the auxiliary is has.',
    },
  ],

  'zero-first-conditionals': [
    {
      kind: 'multiple-choice',
      prompt: 'If you ___ your rights at work, you negotiate with much more confidence.',
      options: ['know', 'will know', 'knew'],
      correctIndex: 0,
      explanation:
        'The zero conditional uses the present simple in both clauses for things that are always true.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'If Bianca gets the certification, she ___ for the senior position.',
      options: ['will apply', 'applies', 'applied'],
      correctIndex: 0,
      explanation:
        'The first conditional is If + present simple, will + infinitive, for real future possibilities.',
    },
    {
      kind: 'fill-blank',
      prompt: 'If it rains tomorrow, we ___ move the class online. (one word)',
      answers: ['will', '’ll', "'ll"],
      explanation: 'The main clause of the first conditional uses will + infinitive.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'If you study hard, you will pass the test.',
        'If you will study hard, you pass the test.',
        'If you study hard you will passed the test.',
      ],
      correctIndex: 0,
      explanation:
        'Will never goes in the if clause — and when the if clause comes first, a comma follows it.',
    },
    {
      kind: 'matching',
      instruction: 'Match each sentence with the conditional it uses:',
      pairs: [
        { left: 'If water reaches 100 degrees, it boils.', right: 'zero — always true' },
        { left: 'If I study today, I’ll go to the party tonight.', right: 'first — possible future' },
        { left: 'You will pass the test if you study hard.', right: 'first — if clause second, no comma' },
      ],
    },
  ],

  'second-conditional': [
    {
      kind: 'multiple-choice',
      prompt: 'If women ___ equal pay everywhere, the world economy would grow.',
      options: ['have', 'had', 'would have'],
      correctIndex: 1,
      explanation: 'In the second conditional, the if clause uses the simple past: If + subject + past form.',
    },
    {
      kind: 'fill-blank',
      prompt: 'If I ___ (be) you, I would apply for that leadership position.',
      answers: ['were'],
      explanation: 'In the second conditional, we use were for all subjects — If I were you is the classic advice structure.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Ana would travel the world by herself if she ___ afraid.',
      options: ['isn’t', 'wasn’t', 'weren’t'],
      correctIndex: 2,
      explanation: 'Unreal situations take were/weren’t for all subjects: ...if she weren’t afraid.',
    },
    {
      kind: 'fill-blank',
      prompt: 'If we supported each other more, we ___ achieve incredible things together.',
      answers: ['would', '’d', "'d"],
      explanation: 'The main clause of the second conditional uses would + verb in the infinitive.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'If I would have more time, I would study English.',
        'If I had more time, I would study English.',
        'If I have more time, I would studied English.',
      ],
      correctIndex: 1,
      explanation: 'Second conditional = If + simple past, would + infinitive. Would never goes in the if clause.',
    },
  ],

  'third-conditional': [
    {
      kind: 'multiple-choice',
      prompt: 'If Laura had asked for the raise, she ___ it — her results spoke for themselves.',
      options: ['would have got', 'would get', 'will have got'],
      correctIndex: 0,
      explanation:
        'The third conditional pairs If + past perfect with would have + participle for past possibilities.',
    },
    {
      kind: 'fill-blank',
      prompt: 'If I ___ (know) my worth earlier, I would have negotiated much harder.',
      answers: ['had known', '’d known', "'d known"],
      explanation: 'The if clause of the third conditional uses the past perfect: had + participle.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'If she had studied, she would have passed the exam.',
        'If she would have studied, she had passed the exam.',
        'If she studied, she would have passed the exam.',
      ],
      correctIndex: 0,
      explanation:
        'Third conditional = If + past perfect, would have + participle — would have never goes in the if clause.',
    },
    {
      kind: 'fill-blank',
      prompt: 'We wouldn’t have missed the flight if we ___ taken a taxi. (one word)',
      answers: ['had', '’d', "'d"],
      explanation: 'Even when the if clause comes second, it still uses had + participle.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'If I’d stayed at university, I ___ have got a master’s degree.',
      options: ['could', 'can', 'will'],
      correctIndex: 0,
      explanation: 'As an alternative to would, the second part can use could have + participle.',
    },
    {
      kind: 'matching',
      instruction: 'Match each sentence with its conditional:',
      pairs: [
        { left: 'If I win the lottery, I will travel around the world.', right: 'first — real possibility' },
        { left: 'If I won the lottery, I would travel around the world.', right: 'second — unreal present' },
        {
          left: 'If I had won the lottery, I would have traveled around the world.',
          right: 'third — past that didn’t happen',
        },
      ],
    },
  ],

  'wishes-and-hopes': [
    {
      kind: 'multiple-choice',
      prompt: 'Ana wishes she ___ more time to study — she runs two businesses.',
      options: ['had', 'has', 'will have'],
      correctIndex: 0,
      explanation:
        'After wish, the grammar goes to the past tense even though the meaning is in the present.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I have an interview tomorrow. I hope I ___ the job.',
      options: ['get', 'got', 'would get'],
      correctIndex: 0,
      explanation:
        'With hope, we use the present simple even when we are talking about the future.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ like more information about the leadership course, please. (one word)',
      answers: ['would', '’d', "'d"],
      explanation: 'I would like expresses a desire — something we want or want to do.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'She wishes she were the manager of that team.',
        'She wishes she is the manager of that team.',
        'She hopes she were the manager of that team.',
      ],
      correctIndex: 0,
      explanation: 'Wish takes the past form (were) for a present situation that isn’t true.',
    },
    {
      kind: 'fill-blank',
      prompt: 'They wish they ___ come to the graduation, but they can’t. (one word)',
      answers: ['could'],
      explanation: 'They wish they could come means that, in reality, they cannot come.',
    },
    {
      kind: 'matching',
      instruction: 'Match each expression with what it describes:',
      pairs: [
        { left: 'wish', right: 'a present situation that isn’t true' },
        { left: 'hope', right: 'a possible future situation' },
        { left: 'would like', right: 'a desire — something we want' },
      ],
    },
  ],

  'active-passive-voice': [
    {
      kind: 'multiple-choice',
      prompt: 'The project ___ by a team of five women — and it won the innovation award.',
      options: ['was led', 'led', 'was leading'],
      correctIndex: 0,
      explanation: 'The passive is BE in the tense of the original verb + the past participle.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which is the passive voice of “Someone built the house in 1899”?',
      options: [
        'The house was built in 1899.',
        'The house built in 1899.',
        'The house has built in 1899.',
      ],
      correctIndex: 0,
      explanation:
        'Built is simple past, so BE goes in the simple past too: was + built.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Your application is ___ (review) right now — you’ll hear back soon.',
      answers: ['being reviewed'],
      explanation:
        'The present continuous passive is is/are being + past participle.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Her research has ___ (publish) in three languages.',
      answers: ['been published'],
      explanation: 'The present perfect passive is has/have been + past participle.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'We choose the passive voice when the focus is on ___.',
      options: [
        'what happened, not who did it',
        'who performed the action',
        'when the action will happen',
      ],
      correctIndex: 0,
      explanation:
        'In the passive, the person or thing that experiences the action becomes the subject, and the agent can disappear.',
    },
    {
      kind: 'matching',
      instruction: 'Match each active sentence with its passive version:',
      pairs: [
        { left: 'She delivers the food.', right: 'The food is delivered.' },
        { left: 'Someone is shipping your package.', right: 'Your package is being shipped.' },
        { left: 'Spielberg has directed the movie ET.', right: 'The movie ET has been directed by Spielberg.' },
      ],
    },
  ],

  'direct-reported-speech': [
    {
      kind: 'multiple-choice',
      prompt: '“I am tired,” she said. → She said that she ___ tired.',
      options: ['was', 'is', 'has been'],
      correctIndex: 0,
      explanation:
        'In reported speech, the tense moves one step back: present simple becomes simple past.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“I will call you tomorrow,” Marta told me. → Marta told me she ___ call me the next day.',
      options: ['would', 'will', 'did'],
      correctIndex: 0,
      explanation: 'Will becomes would in reported speech, and tomorrow becomes the next day.',
    },
    {
      kind: 'fill-blank',
      prompt: '“I have been to Spain,” he told me. → He told me that he ___ been to Spain. (one word)',
      answers: ['had', '’d', "'d"],
      explanation: 'The present perfect moves back to the past perfect in reported speech.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which is the correct reported version of “Bill arrived on Saturday,” he said?',
      options: [
        'He said that Bill had arrived on Saturday.',
        'He said that Bill has arrived on Saturday.',
        'He said that Bill arrives on Saturday.',
      ],
      correctIndex: 0,
      explanation: 'The simple past moves one tense back, to the past perfect.',
    },
    {
      kind: 'matching',
      instruction: 'Match each direct-speech time expression with its reported-speech version:',
      pairs: [
        { left: 'now', right: 'then' },
        { left: 'yesterday', right: 'the day before' },
        { left: 'tomorrow', right: 'the following day' },
        { left: 'last week', right: 'the week before' },
      ],
    },
    {
      kind: 'fill-blank',
      prompt:
        '“We have been waiting for hours,” they complained. → They complained that they ___ waiting for hours.',
      answers: ['had been', '’d been', "'d been"],
      explanation:
        'The present perfect continuous becomes the past perfect continuous: had been + -ing.',
    },
  ],

  'academic-writing-tenses': [
    {
      kind: 'multiple-choice',
      prompt: 'In academic writing, existing facts and current theories are described in the ___.',
      options: ['present simple', 'past simple', 'future simple'],
      correctIndex: 0,
      explanation:
        'The present tense dominates academic work because it describes current facts and states of being.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The results ___ that access to education changes women’s lives.',
      options: ['demonstrate', 'demonstrated', 'will demonstrate'],
      correctIndex: 0,
      explanation:
        'The findings of a study take the present simple while their conclusions are still relevant.',
    },
    {
      kind: 'multiple-choice',
      prompt:
        'Early studies ___ that women were less suited to leadership, although this has since been disputed by many experts.',
      options: ['claimed', 'claim', 'will claim'],
      correctIndex: 0,
      explanation:
        'The past tense signals that an idea is not widely accepted any more.',
    },
    {
      kind: 'fill-blank',
      prompt: 'In the methods section: The sample ___ tested using several techniques. (one word)',
      answers: ['was'],
      explanation:
        'Methods of a completed experiment are described in the past, usually in the passive voice.',
    },
    {
      kind: 'fill-blank',
      prompt:
        'In a research proposal: This study ___ examine the role of mentorship in women’s careers. (one word)',
      answers: ['will'],
      explanation:
        'Research proposals use the future tense to describe aims and methods that haven’t happened yet.',
    },
    {
      kind: 'matching',
      instruction: 'Match each tense with its main role in academic writing:',
      pairs: [
        { left: 'present simple', right: 'current facts, findings, and claims' },
        { left: 'past simple', right: 'methods and ideas no longer accepted' },
        { left: 'future simple', right: 'proposals and future research' },
      ],
    },
  ],
};
